import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useScrollIntoViewIfNotVisible } from "../../../hooks/useScrollIntoViewIfNotVisible";
import { flushSync } from "react-dom";
import { useTranslation } from "@fmc-digital/i18n";
import {
  Button,
  ErrorBanner,
  ErrorMessage,
  MFACodeInput,
  type MFACodeInputRef,
} from "@fmc-digital/ui";
import { InfoModal } from "../../atoms/InfoModal";
import { MFADestinationText } from "../../molecules/MFADestinationText";
import { MFAResendLink } from "../../molecules/MFAResendLink";
import { MFATimer } from "../../molecules/MFATimer";
import type { MFACodeContainerProps } from "./mfa-code-container.types";
import {
  MFA_WAIT_SECONDS,
  MFA_EXPIRATION_MINUTES,
  LOGIN_MFA_EXPIRATION_MINUTES,
} from "./mfa-code-container.constants";
import { trackLoginEvent, LoginEvent } from "@/utils/analytics.utils";

function computeRemainingSeconds(expiresAtMs: number): number {
  return Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
}

function toTimerParts(totalSeconds: number): { minutes: number; seconds: number } {
  return {
    minutes: Math.floor(totalSeconds / 60),
    seconds: totalSeconds % 60,
  };
}

/**
 * MFACodeContainer Organism
 * Container component for MFA code verification
 */
export function MFACodeContainer({
  factorState,
  factorsList,
  onVerify,
  onCancel,
  onNavigateToFactorSelection,
  onResend,
  error,
  errorCode,
  onErrorClear,
  isLoading = false,
  flow = "recovery",
  stateTokenExpiresAt,
  onStateTokenExpired,
}: Readonly<MFACodeContainerProps>) {
  const { t } = useTranslation();
  const isLoginFlow = flow === "login";
  const effectiveStateTokenExpiresAt = stateTokenExpiresAt;
  const usesStateTokenExpiry = effectiveStateTokenExpiresAt != null;
  const timerMode = useMemo((): "dynamic" | "static" | "hidden" => {
    if (effectiveStateTokenExpiresAt != null) {
      return "dynamic";
    }
    if (isLoginFlow) {
      return "static";
    }
    return "static";
  }, [effectiveStateTokenExpiresAt, isLoginFlow]);
  const initialExpirationMinutes = isLoginFlow
    ? LOGIN_MFA_EXPIRATION_MINUTES
    : MFA_EXPIRATION_MINUTES;
  const [code, setCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [waitSeconds, setWaitSeconds] = useState(MFA_WAIT_SECONDS);
  const [expirationMinutes, setExpirationMinutes] = useState(initialExpirationMinutes);
  const [expirationSeconds, setExpirationSeconds] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [tick, setTick] = useState(0);
  const expirationMinutesRef = useRef(initialExpirationMinutes);
  const hasMarkedExpiredRef = useRef(false);
  const codeInputRef = useRef<MFACodeInputRef>(null);

  const [selectedFactorType] = useState<"email" | "sms">(
    factorState.factorType ?? "email",
  );
  const [selectedProfile] = useState<{
    email?: string;
    phoneNumber?: string;
  }>(factorState.profile ?? {});

  const markExpired = useCallback(() => {
    if (hasMarkedExpiredRef.current) {
      return;
    }
    hasMarkedExpiredRef.current = true;
    flushSync(() => {
      setCode(["", "", "", "", "", ""]);
      setIsExpired(true);
    });
    if (isLoginFlow) {
      trackLoginEvent(LoginEvent.TIMER_EXPIRED);
    }
    if (usesStateTokenExpiry || isLoginFlow) {
      setShowExpiryModal(true);
    }
  }, [isLoginFlow, usesStateTokenExpiry]);

  const resetExpiryTracking = useCallback(() => {
    hasMarkedExpiredRef.current = false;
    setIsExpired(false);
    setShowExpiryModal(false);
  }, []);

  const timerDisplay = useMemo(() => {
    if (usesStateTokenExpiry && effectiveStateTokenExpiresAt != null) {
      return toTimerParts(computeRemainingSeconds(effectiveStateTokenExpiresAt));
    }
    return { minutes: expirationMinutes, seconds: expirationSeconds };
  }, [
    usesStateTokenExpiry,
    effectiveStateTokenExpiresAt,
    expirationMinutes,
    expirationSeconds,
    tick,
  ]);

  const { minutes: timerMinutes, seconds: timerSeconds } = timerDisplay;

  // Keep ref in sync with expirationMinutes state
  useEffect(() => {
    expirationMinutesRef.current = expirationMinutes;
  }, [expirationMinutes]);

  // Timer for wait period
  useEffect(() => {
    if (waitSeconds > 0) {
      const timer = setTimeout(() => setWaitSeconds(waitSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [waitSeconds]);

  // Dynamic tick for login flow (Okta stateToken expiry)
  useEffect(() => {
    if (!usesStateTokenExpiry || effectiveStateTokenExpiresAt == null) {
      return;
    }

    const timer = setInterval(() => {
      setTick((currentTick) => currentTick + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [effectiveStateTokenExpiresAt, usesStateTokenExpiry]);

  useEffect(() => {
    if (!usesStateTokenExpiry || effectiveStateTokenExpiresAt == null) {
      return;
    }

    resetExpiryTracking();
  }, [resetExpiryTracking, effectiveStateTokenExpiresAt, usesStateTokenExpiry]);

  useEffect(() => {
    if (!usesStateTokenExpiry || effectiveStateTokenExpiresAt == null) {
      return;
    }

    const remaining = computeRemainingSeconds(effectiveStateTokenExpiresAt);
    if (remaining !== 0) {
      return;
    }

    queueMicrotask(() => {
      markExpired();
    });
  }, [
    markExpired,
    effectiveStateTokenExpiresAt,
    usesStateTokenExpiry,
    tick,
  ]);

  // Static timer when Okta stateTokenExpiresAt is unavailable (recovery + login fallback)
  useEffect(() => {
    if (usesStateTokenExpiry) {
      return;
    }

    const handleTimerTick = () => {
      setExpirationSeconds((prevSeconds) => {
        if (prevSeconds === 0) {
          return handleMinuteDecrement();
        }
        return prevSeconds - 1;
      });
    };

    const handleMinuteDecrement = (): number => {
      if (expirationMinutesRef.current === 0) {
        queueMicrotask(() => {
          markExpired();
        });
        return 0;
      }
      setExpirationMinutes((prevMinutes) => {
        expirationMinutesRef.current =
          prevMinutes > 0 ? prevMinutes - 1 : 0;
        return expirationMinutesRef.current;
      });
      return 59;
    };

    const timer = setInterval(handleTimerTick, 1000);

    return () => clearInterval(timer);
  }, [markExpired, usesStateTokenExpiry]);

  const handleNext = () => {
    const fullCode = code.join("");
    if (fullCode.length === 6) {
      onVerify(fullCode, {
        ...factorState,
        factorType: selectedFactorType,
        profile: selectedProfile,
      });
    }
  };

  const handleSendToAlternate = () => {
    const targetFactorType = selectedFactorType === "sms" ? "email" : "sms";

    if (factorsList && factorsList.length > 0) {
      const alternateFactor = factorsList.find((factor) => {
        const factorType = factor.factorType?.toLowerCase() ?? "";
        if (targetFactorType === "email") {
          return factorType.includes("email");
        }
        return factorType.includes("sms");
      });

      if (alternateFactor) {
        onNavigateToFactorSelection(targetFactorType);
      }
    } else {
      onNavigateToFactorSelection(targetFactorType);
    }
  };

  const handleResend = () => {
    if (onErrorClear) {
      onErrorClear();
    }
    setWaitSeconds(MFA_WAIT_SECONDS);

    if (usesStateTokenExpiry || isLoginFlow) {
      resetExpiryTracking();
    } else {
      expirationMinutesRef.current = MFA_EXPIRATION_MINUTES;
      setExpirationMinutes(MFA_EXPIRATION_MINUTES);
      setExpirationSeconds(0);
      resetExpiryTracking();
    }

    setCode(["", "", "", "", "", ""]);

    setTimeout(() => {
      codeInputRef.current?.focus();
    }, 0);

    onResend();
  };

  const handleStateTokenExpiredConfirm = () => {
    setShowExpiryModal(false);
    onStateTokenExpired?.();
  };

  const handleCodeChange = (newCode: string[]) => {
    setCode(newCode);
    if (error && onErrorClear) {
      onErrorClear();
    }
  };

  const getAlternateOptionText = () => {
    if (selectedFactorType === "sms") {
      return t("auth.mfa.sendToEmail");
    }
    return t("auth.mfa.sendToPhone");
  };

  const hasAlternateFactor = () => {
    if (!factorsList || factorsList.length <= 1) {
      return false;
    }

    const targetFactorType = selectedFactorType === "sms" ? "email" : "sms";
    return factorsList.some((factor) => {
      const factorType = factor.factorType?.toLowerCase() ?? "";
      if (targetFactorType === "email") {
        return factorType.includes("email");
      }
      return factorType.includes("sms");
    });
  };

  const isCodeComplete = code.every((digit) => digit !== "");
  const isInvalidCodeError = errorCode === "CUST_S1200004";

  const errorBannerRef = useRef<HTMLDivElement>(null);
  const expiredBannerRef = useRef<HTMLDivElement>(null);
  useScrollIntoViewIfNotVisible(errorBannerRef, !!error && !isInvalidCodeError);
  useScrollIntoViewIfNotVisible(
    expiredBannerRef,
    isExpired && !usesStateTokenExpiry && !isLoginFlow,
  );

  const passcodeExpiredTitle = t(
    "auth.login.passcodeExpiredTitle",
    "Passcode Expired",
  );
  const passcodeExpiredBody = t(
    "auth.login.passcodeExpiredBody",
    "Your login token has expired. Please log in again.",
  );

  return (
    <div className="mfa-code-wrapper w-full mx-auto px-4 md:px-0" data-testid="mfa-code-container">
      <div className="text-center mb-8 md:mb-[32px]">
        <MFADestinationText
          factorType={selectedFactorType}
          profile={selectedProfile}
        />
        <MFAResendLink
          waitSeconds={isExpired ? 0 : waitSeconds}
          onResend={handleResend}
        />
      </div>

      <div className="auth-card w-full mx-auto">
        <div className="mfa-code-content-wrapper">
          <div className="mfa-otp-stack">
            <MFACodeInput
              ref={codeInputRef}
              code={code}
              onChange={handleCodeChange}
              error={!!error}
              disabled={isExpired}
            />
            {error && isInvalidCodeError && (
              <div className="mfa-invalid-code-error-wrap">
                <ErrorMessage
                  className="mfa-invalid-code-error"
                  data-testid="mfa-invalid-code-error"
                >
                  {error}
                </ErrorMessage>
              </div>
            )}
            {!isExpired && (
              <div data-timer-mode={timerMode}>
                <MFATimer minutes={timerMinutes} seconds={timerSeconds} />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-8 mfa-code-buttons-wrapper">
          <Button
            variant="outlined"
            onClick={onCancel}
            className="w-full lg:flex-1"
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="filled"
            onClick={handleNext}
            className="w-full lg:flex-1"
            disabled={!isCodeComplete || !!error || isExpired || isLoading}
            loading={isLoading}
          >
            {t("common.next")}
          </Button>
        </div>

        {hasAlternateFactor() && (
          <div className="text-center mfa-alternate-factor-link">
            <button
              type="button"
              onClick={handleSendToAlternate}
              className="mfa-resend-link"
            >
              {getAlternateOptionText()}
            </button>
          </div>
        )}
      </div>

      {error && !isInvalidCodeError && (
        <div ref={errorBannerRef}>
          <ErrorBanner message={error} className="mt-6 md:mt-8 mb-8" />
        </div>
      )}
      {isExpired && !usesStateTokenExpiry && !isLoginFlow && (
        <div ref={expiredBannerRef}>
          <ErrorBanner
            message={t("errors.codeExpired")}
            className="mt-6 md:mt-8 mb-8"
          />
        </div>
      )}

      {isLoginFlow && (
        <InfoModal
          open={showExpiryModal}
          onClose={handleStateTokenExpiredConfirm}
          title={passcodeExpiredTitle}
          body={passcodeExpiredBody}
          variant="loginSessionExpiry"
          hideSecondaryAction
          showCloseButton={false}
          closeOnOverlayClick={false}
          closeOnEscape={false}
          primaryAction={{
            label: t("auth.login.passcodeExpiredLogin", "Login"),
            onClick: handleStateTokenExpiredConfirm,
          }}
        />
      )}
    </div>
  );
}
